/**
 * AEGIS-9 voice_calls seed.
 *
 * Populates the kairos-demo tenant with 18 realistic voice interactions
 * so the VoiceDashboard has immediate signal: inbound + outbound mix,
 * every intent represented, one frustrated-caller escalation, one
 * unanswered outbound, and one Spanish-language call.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const now = new Date();
const hoursAgo = (h) => new Date(now.getTime() - h * 3600000).toISOString();
const minutesAgo = (m) => new Date(now.getTime() - m * 60000).toISOString();

const base = (extra = {}) => ({
  tenantId: DEMO_TENANT_ID,
  createdAt: hoursAgo(2),
  createdBy: 'aegis_auto',
  updatedAt: hoursAgo(2),
  updatedBy: 'aegis_auto',
  isDeleted: false,
  softDeleted: false,
  version: 1,
  source: 'voice_seed',
  aegisPersona: 'Kira',
  language: 'en-US',
  blandCallId: null,
  ...extra,
});

function t(role, text) { return { role, at: new Date().toISOString(), text }; }

export const SEED_VOICE_CALLS = [
  // ─── Inbound carrier check call (auto-handled) ───────────────────────
  {
    id: 'vc-001', callId: 'vc-001',
    direction: 'inbound', intent: 'check_call',
    status: 'completed', handledBy: 'aegis',
    callerType: 'carrier', fromNumber: '+1-214-555-2201', toNumber: '+1-800-555-AEGIS',
    startedAt: minutesAgo(12), endedAt: minutesAgo(9), durationSec: 173,
    relatedLoadId: 'load-00003', relatedCarrierId: 'car-swft', relatedCustomerId: 'cu-001',
    summary: 'Swift driver checked in — currently east of Amarillo, ETA Tuesday 7am. No issues.',
    transcript: [
      t('aegis', 'Kira from Watchmen Logistics, thanks for calling. Which load are you checking in on?'),
      t('caller', 'Yeah, uh, load WC-10703, this is Swift, truck 4821.'),
      t('aegis', 'Got it, WC-10703 Dallas to LA. What\'s your current location and ETA?'),
      t('caller', 'About 40 miles east of Amarillo on I-40. Running clean, should hit the LA yard Tuesday 7 AM.'),
      t('aegis', 'Perfect — Tuesday 7 AM arrival. Any issues, delays, or accessorials to flag?'),
      t('caller', 'Nah, all smooth.'),
      t('aegis', 'Appreciate it. I\'ve logged the check call and updated the customer ETA. Safe travels.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.55,
    outcome: { kind: 'check_call_logged', eta: 'Tuesday 07:00', locationNote: '40 mi east of Amarillo, I-40' },
    ...base({ createdAt: minutesAgo(12), updatedAt: minutesAgo(9) }),
  },

  // ─── Outbound pickup confirmation (auto) ─────────────────────────────
  {
    id: 'vc-002', callId: 'vc-002',
    direction: 'outbound', intent: 'pickup_confirmation',
    status: 'completed', handledBy: 'aegis',
    callerType: 'carrier', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-901-555-0174',
    startedAt: hoursAgo(1.7), endedAt: hoursAgo(1.65), durationSec: 142,
    relatedLoadId: 'load-00014', relatedCarrierId: 'car-wern',
    summary: 'Werner confirmed pickup for WC-10714 at 14:00. Dock door 12.',
    transcript: [
      t('aegis', 'Hi, this is Kira calling from Watchmen Logistics to confirm pickup on load WC-10714 at the Memphis DC at 2 PM today.'),
      t('caller', 'Yep, I\'m 20 minutes out. Dock 12 right?'),
      t('aegis', 'That\'s correct, dock 12. Anything you need from us before pickup?'),
      t('caller', 'Nope, we\'re good.'),
      t('aegis', 'Great — I\'ll let the dock know you\'re inbound. Thanks!'),
    ],
    sentiment: 'positive', sentimentScore: 0.8,
    outcome: { kind: 'pickup_confirmed', confirmedFor: hoursAgo(-0.3), dockDoor: 12 },
    ...base({ createdAt: hoursAgo(1.7), updatedAt: hoursAgo(1.65) }),
  },

  // ─── Inbound driver exception report (auto, flagged for follow-up) ───
  {
    id: 'vc-003', callId: 'vc-003',
    direction: 'inbound', intent: 'exception_report',
    status: 'completed', handledBy: 'aegis',
    callerType: 'driver', fromNumber: '+1-214-555-0042', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(2.1), endedAt: hoursAgo(2.04), durationSec: 204,
    relatedLoadId: 'load-00013', relatedCarrierId: 'car-ns',
    summary: 'Rail mechanical issue at Louisville — estimated 4h delay. Exception logged, dispatcher notified.',
    transcript: [
      t('aegis', 'Kira from Watchmen Logistics — what can I help you with?'),
      t('caller', 'Got a problem on WC-10712. Rail sprang a mechanical issue at Louisville. NS is calling a swap crew.'),
      t('aegis', 'Understood — mechanical issue at Louisville. What\'s the estimated delay?'),
      t('caller', 'Four hours minimum. Maybe six depending on parts.'),
      t('aegis', 'Logged. I\'m opening an exception, notifying dispatch, and the customer will get a proactive ETA update from us. Anything else you need?'),
      t('caller', 'Nope, just wanted to get it on the record.'),
      t('aegis', 'Appreciate you calling it in early. We\'ll take it from here.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.4,
    outcome: { kind: 'exception_opened', exceptionType: 'rail_delay', delayHours: 4, notifyDispatch: true, notifyCustomer: true },
    ...base({ createdAt: hoursAgo(2.1), updatedAt: hoursAgo(2.04) }),
  },

  // ─── Inbound customer status call (auto) ─────────────────────────────
  {
    id: 'vc-004', callId: 'vc-004',
    direction: 'inbound', intent: 'customer_status',
    status: 'completed', handledBy: 'aegis',
    callerType: 'customer', fromNumber: '+1-305-555-0108', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(3.5), endedAt: hoursAgo(3.47), durationSec: 98,
    relatedLoadId: 'load-00003', relatedCustomerId: 'cu-001',
    summary: 'Steve Madden ops asked about WC-10703 status — gave live location, ETA, and carrier info.',
    transcript: [
      t('aegis', 'Kira from Watchmen Logistics — how can I help?'),
      t('caller', 'Hi, this is Alex from Steve Madden, checking on WC-10703.'),
      t('aegis', 'Of course. WC-10703 is currently east of Amarillo on I-40, tracking to deliver Tuesday around 7 AM Pacific. Swift Transportation is the carrier, driver Marcus Delgado. Everything is running clean so far.'),
      t('caller', 'Perfect, thanks.'),
      t('aegis', 'My pleasure — I\'ll send you the tracking link by email. Have a good day.'),
    ],
    sentiment: 'positive', sentimentScore: 0.75,
    outcome: { kind: 'status_shared', sentTrackingLink: true },
    ...base({ createdAt: hoursAgo(3.5), updatedAt: hoursAgo(3.47) }),
  },

  // ─── Inbound customer — frustrated, ESCALATED ────────────────────────
  {
    id: 'vc-005', callId: 'vc-005',
    direction: 'inbound', intent: 'customer_status',
    status: 'escalated', handledBy: 'human',
    callerType: 'customer', fromNumber: '+1-305-555-0212', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(5), endedAt: hoursAgo(4.92), durationSec: 287,
    relatedLoadId: 'load-00014', relatedCustomerId: 'cu-005',
    summary: 'Wild Fork Foods ops escalated — frustrated about repeated SLA misses on Memphis-Atlanta lane. Transferred with full context.',
    transcript: [
      t('aegis', 'Kira from Watchmen Logistics — how can I help?'),
      t('caller', 'You can help by telling me why my lane is behind AGAIN. This is the third time this month.'),
      t('aegis', 'I hear you — I pulled up your account. You\'re Wild Fork Foods, right? I see recent slippage on Memphis-Atlanta. Let me connect you with Sarah Kim, your account manager, she\'s best-placed to walk through the pattern and what we\'re changing. Stay on the line while I transfer and brief her.'),
      t('caller', 'Fine. Please be fast.'),
      t('aegis', '(transferring with context summary)'),
    ],
    sentiment: 'frustrated', sentimentScore: 0.2,
    outcome: { kind: 'escalated_to_human', toUserId: 'user-004', reason: 'frustrated_repeat_issue' },
    escalationReason: 'Caller sentiment frustrated; repeated SLA pattern flagged. Transferring to account manager with context summary prepared.',
    escalationTo: 'user-004',
    ...base({ createdAt: hoursAgo(5), updatedAt: hoursAgo(4.9) }),
  },

  // ─── Outbound delay notification (auto) ──────────────────────────────
  {
    id: 'vc-006', callId: 'vc-006',
    direction: 'outbound', intent: 'delay_notification',
    status: 'completed', handledBy: 'aegis',
    callerType: 'customer', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-404-555-0133',
    startedAt: hoursAgo(1.5), endedAt: hoursAgo(1.45), durationSec: 156,
    relatedLoadId: 'load-00013', relatedCustomerId: 'cu-002',
    summary: 'Proactive delay notification — 4h rail delay acknowledged, customer asked for ops to email written update.',
    transcript: [
      t('aegis', 'Hi, this is Kira from Watchmen Logistics with a proactive update on your shipment WC-10712. The rail partner hit a mechanical issue at Louisville that\'s estimated to push delivery about 4 hours. We\'re working the swap crew now.'),
      t('caller', 'Appreciate the heads up. Can you email me the written update as well?'),
      t('aegis', 'Absolutely — emailing you right now with the timeline and next-update window. Anything else?'),
      t('caller', 'No, that\'s all, thanks.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.6,
    outcome: { kind: 'delay_notified', emailSent: true },
    ...base({ createdAt: hoursAgo(1.5), updatedAt: hoursAgo(1.45) }),
  },

  // ─── Outbound delivery notification ──────────────────────────────────
  {
    id: 'vc-007', callId: 'vc-007',
    direction: 'outbound', intent: 'delivery_notification',
    status: 'completed', handledBy: 'aegis',
    callerType: 'customer', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-615-555-0141',
    startedAt: hoursAgo(6), endedAt: hoursAgo(5.97), durationSec: 88,
    relatedLoadId: 'load-00038', relatedCustomerId: 'cu-003',
    summary: 'Out-for-delivery notification accepted — consignee confirmed receiver on site.',
    transcript: [
      t('aegis', 'Hi, this is Kira from Watchmen Logistics. Your shipment WC-10738 is out for delivery, ETA in about 90 minutes. Anything we should tell the driver?'),
      t('caller', 'Nope, dock is open, Tom will sign.'),
      t('aegis', 'Great — relaying that to the driver. Thanks!'),
    ],
    sentiment: 'positive', sentimentScore: 0.85,
    outcome: { kind: 'delivery_confirmed_ready', receiverName: 'Tom' },
    ...base({ createdAt: hoursAgo(6), updatedAt: hoursAgo(5.97) }),
  },

  // ─── Outbound pickup confirmation — carrier couldn't make it ─────────
  {
    id: 'vc-008', callId: 'vc-008',
    direction: 'outbound', intent: 'pickup_confirmation',
    status: 'escalated', handledBy: 'aegis',
    callerType: 'carrier', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-214-555-0303',
    startedAt: hoursAgo(4), endedAt: hoursAgo(3.93), durationSec: 156,
    relatedLoadId: 'load-00025', relatedCarrierId: 'car-usat',
    summary: 'USAT flagged driver down — cannot cover pickup. Escalated to dispatch to re-tender.',
    transcript: [
      t('aegis', 'Hi, Kira from Watchmen Logistics — confirming pickup on WC-10725 at 4 PM today?'),
      t('caller', 'No, we got a driver-down situation — that load is at risk. I don\'t have coverage.'),
      t('aegis', 'Understood — that\'s a coverage gap. I\'m flagging this to dispatch right now to re-tender. Dispatch will call you back within 10 minutes to confirm the hand-off.'),
      t('caller', 'Thanks.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.35,
    outcome: { kind: 'pickup_at_risk', reason: 'carrier_driver_down', action: 're_tender' },
    escalationReason: 'Carrier cannot cover pickup — re-tender required.',
    escalationTo: 'user-005',
    ...base({ createdAt: hoursAgo(4), updatedAt: hoursAgo(3.9) }),
  },

  // ─── Inbound driver delivery confirmation ────────────────────────────
  {
    id: 'vc-009', callId: 'vc-009',
    direction: 'inbound', intent: 'delivery_confirmation',
    status: 'completed', handledBy: 'aegis',
    callerType: 'driver', fromNumber: '+1-214-555-0058', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(8), endedAt: hoursAgo(7.97), durationSec: 97,
    relatedLoadId: 'load-00046', relatedCarrierId: 'car-ownedfleet',
    summary: 'Driver confirmed WC-10745 delivered to Charlotte 14:12, POD signed by Mike at Pinnacle.',
    transcript: [
      t('aegis', 'Kira here — what\'s the update?'),
      t('caller', 'WC-10745, delivered Charlotte, 2:12 PM, Mike at the dock signed.'),
      t('aegis', 'Got it — delivered 2:12, POD signed by Mike. Uploading to your load record and invoicing will trigger automatically. Thanks!'),
    ],
    sentiment: 'positive', sentimentScore: 0.7,
    outcome: { kind: 'pod_logged', signedBy: 'Mike', actualDeliveryAt: hoursAgo(8.1) },
    ...base({ createdAt: hoursAgo(8), updatedAt: hoursAgo(7.97) }),
  },

  // ─── Outbound exception notification — customer asked to reschedule ──
  {
    id: 'vc-010', callId: 'vc-010',
    direction: 'outbound', intent: 'delay_notification',
    status: 'completed', handledBy: 'aegis',
    callerType: 'customer', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-954-555-0199',
    startedAt: hoursAgo(22), endedAt: hoursAgo(21.95), durationSec: 214,
    relatedLoadId: 'load-00008', relatedCustomerId: 'cu-005',
    summary: 'Customer asked to reschedule delivery from Friday morning to Monday morning due to warehouse closure.',
    transcript: [
      t('aegis', 'Hi, this is Kira from Watchmen Logistics with a delivery update for WC-10708.'),
      t('caller', 'Actually, can you reschedule to Monday? Friday our warehouse is closed.'),
      t('aegis', 'No problem — moving delivery to Monday 8 AM. The carrier will hold overnight and re-deliver Monday. Anything else?'),
      t('caller', 'That\'s perfect.'),
    ],
    sentiment: 'positive', sentimentScore: 0.75,
    outcome: { kind: 'rescheduled', newDeliveryAt: 'Monday 08:00' },
    ...base({ createdAt: hoursAgo(22), updatedAt: hoursAgo(21.95) }),
  },

  // ─── Inbound driver — Spanish language ───────────────────────────────
  {
    id: 'vc-011', callId: 'vc-011',
    direction: 'inbound', intent: 'check_call',
    status: 'completed', handledBy: 'aegis',
    callerType: 'driver', fromNumber: '+1-956-555-0183', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(9), endedAt: hoursAgo(8.96), durationSec: 128,
    relatedLoadId: 'load-00021', relatedCarrierId: 'car-indep',
    summary: 'Spanish-language check call on WC-10721 — driver near Dallas, on schedule.',
    language: 'es-MX',
    transcript: [
      t('aegis', 'Hola, soy Kira de Watchmen Logistics. ¿En qué carga nos reportas?'),
      t('caller', 'La WC-10721, estoy saliendo de Laredo.'),
      t('aegis', '¿Cuál es tu ETA a Dallas y algún incidente?'),
      t('caller', 'Sin problemas, llego mañana a las 8 de la mañana.'),
      t('aegis', 'Gracias — registrado. Buen viaje.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.6,
    outcome: { kind: 'check_call_logged', eta: 'mañana 08:00' },
    ...base({ createdAt: hoursAgo(9), updatedAt: hoursAgo(8.96), language: 'es-MX' }),
  },

  // ─── Outbound pickup confirmation — no answer ────────────────────────
  {
    id: 'vc-012', callId: 'vc-012',
    direction: 'outbound', intent: 'pickup_confirmation',
    status: 'failed', handledBy: 'aegis',
    callerType: 'carrier', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-713-555-0410',
    startedAt: hoursAgo(11), endedAt: hoursAgo(10.98), durationSec: 18,
    relatedLoadId: 'load-00018', relatedCarrierId: 'car-unknown',
    summary: 'No answer after 3 attempts — routed to dispatcher for manual follow-up.',
    transcript: [
      t('aegis', '(attempt 1 — no answer)'),
      t('aegis', '(attempt 2 — voicemail)'),
      t('aegis', '(attempt 3 — no answer; routing to dispatcher)'),
    ],
    sentiment: null, sentimentScore: null,
    outcome: { kind: 'no_answer_escalated', attempts: 3 },
    escalationReason: 'No answer after 3 attempts — manual follow-up required.',
    escalationTo: 'user-002',
    ...base({ createdAt: hoursAgo(11), updatedAt: hoursAgo(10.97) }),
  },

  // ─── Queued outbound — pending pickup confirmation ───────────────────
  {
    id: 'vc-013', callId: 'vc-013',
    direction: 'outbound', intent: 'pickup_confirmation',
    status: 'queued', handledBy: 'aegis',
    callerType: 'carrier', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-615-555-0344',
    startedAt: minutesAgo(5), endedAt: null, durationSec: null,
    relatedLoadId: 'load-00030', relatedCarrierId: 'car-werner',
    summary: 'Scheduled pickup confirmation — queued for Werner at Nashville DC.',
    transcript: [],
    sentiment: null, sentimentScore: null,
    outcome: null,
    ...base({ createdAt: minutesAgo(5), updatedAt: minutesAgo(5) }),
  },

  // ─── Inbound carrier reschedule request ──────────────────────────────
  {
    id: 'vc-014', callId: 'vc-014',
    direction: 'inbound', intent: 'other',
    status: 'completed', handledBy: 'human',
    callerType: 'carrier', fromNumber: '+1-214-555-0501', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(14), endedAt: hoursAgo(13.9), durationSec: 342,
    relatedLoadId: 'load-00011', relatedCarrierId: 'car-pattyman',
    summary: 'Carrier asked about contract negotiation — routed straight to broker on duty.',
    transcript: [
      t('aegis', 'Kira from Watchmen Logistics — what\'s up?'),
      t('caller', 'I want to renegotiate our contract rates.'),
      t('aegis', 'That\'s a broker conversation — let me transfer you to Kevin Patel, our carrier manager.'),
      t('caller', 'Thanks.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.5,
    outcome: { kind: 'routed_to_broker', toUserId: 'user-005' },
    escalationReason: 'Out-of-scope for AEGIS autonomous handling — transferred to broker.',
    escalationTo: 'user-005',
    ...base({ createdAt: hoursAgo(14), updatedAt: hoursAgo(13.9) }),
  },

  // ─── Inbound driver — delivery exception (damage) ────────────────────
  {
    id: 'vc-015', callId: 'vc-015',
    direction: 'inbound', intent: 'exception_report',
    status: 'completed', handledBy: 'aegis',
    callerType: 'driver', fromNumber: '+1-214-555-0301', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(18), endedAt: hoursAgo(17.93), durationSec: 262,
    relatedLoadId: 'load-00039', relatedCarrierId: 'car-swft',
    summary: 'Damage report on WC-10739 — 2 cases crushed. Exception opened, claims flagged, photos requested.',
    transcript: [
      t('aegis', 'Kira here — how can I help?'),
      t('caller', 'We got damage on WC-10739. Two cases crushed during transfer at Memphis.'),
      t('aegis', 'I\'m opening a damage exception. Can you upload photos to the load record through the driver app?'),
      t('caller', 'Yeah, doing it now.'),
      t('aegis', 'Perfect — claims notified and the customer will be notified. Continue to destination and the receiver will note the exception at delivery.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.45,
    outcome: { kind: 'damage_exception', cases: 2, claimsNotified: true },
    ...base({ createdAt: hoursAgo(18), updatedAt: hoursAgo(17.93) }),
  },

  // ─── Historical inbound check call (yesterday) ───────────────────────
  {
    id: 'vc-016', callId: 'vc-016',
    direction: 'inbound', intent: 'check_call',
    status: 'completed', handledBy: 'aegis',
    callerType: 'carrier', fromNumber: '+1-214-555-0621', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(26), endedAt: hoursAgo(25.96), durationSec: 145,
    relatedLoadId: 'load-00004', relatedCarrierId: 'car-schneider',
    summary: 'Routine check call — Schneider driver on track for Dallas arrival tomorrow.',
    transcript: [
      t('aegis', 'Kira from Watchmen — load?'),
      t('caller', 'WC-10704, Schneider, truck 7715.'),
      t('aegis', 'What\'s your ETA?'),
      t('caller', 'Dallas tomorrow, 0600.'),
      t('aegis', 'Confirmed, logged.'),
    ],
    sentiment: 'neutral', sentimentScore: 0.6,
    outcome: { kind: 'check_call_logged' },
    ...base({ createdAt: hoursAgo(26), updatedAt: hoursAgo(25.96) }),
  },

  // ─── Outbound delivery confirmation to customer ──────────────────────
  {
    id: 'vc-017', callId: 'vc-017',
    direction: 'outbound', intent: 'delivery_notification',
    status: 'completed', handledBy: 'aegis',
    callerType: 'customer', fromNumber: '+1-800-555-AEGIS', toNumber: '+1-704-555-0441',
    startedAt: hoursAgo(30), endedAt: hoursAgo(29.97), durationSec: 92,
    relatedLoadId: 'load-00046', relatedCustomerId: 'cu-004',
    summary: 'Customer ack on delivery confirmation — asked to skip ETA calls on this account going forward.',
    transcript: [
      t('aegis', 'Hi, Kira from Watchmen — your load WC-10745 delivered on time at 2:12 PM.'),
      t('caller', 'Great. Actually, we prefer email-only for delivery confirmations going forward.'),
      t('aegis', 'Updating your preference now — all future deliveries will confirm by email. Thanks!'),
    ],
    sentiment: 'positive', sentimentScore: 0.7,
    outcome: { kind: 'pref_updated', channel: 'email_only_on_delivery' },
    ...base({ createdAt: hoursAgo(30), updatedAt: hoursAgo(29.97) }),
  },

  // ─── Inbound customer — thanks (positive signal) ─────────────────────
  {
    id: 'vc-018', callId: 'vc-018',
    direction: 'inbound', intent: 'other',
    status: 'completed', handledBy: 'aegis',
    callerType: 'customer', fromNumber: '+1-305-555-0188', toNumber: '+1-800-555-AEGIS',
    startedAt: hoursAgo(36), endedAt: hoursAgo(35.99), durationSec: 52,
    relatedCustomerId: 'cu-005',
    summary: 'Customer thanked the team for a quick recovery — positive signal logged against account.',
    transcript: [
      t('aegis', 'Kira from Watchmen — how can I help?'),
      t('caller', 'I just wanted to say thanks — your team pulled through on our last-minute reroute.'),
      t('aegis', 'That means a lot — I\'ll pass that along to Sarah. Have a great day!'),
    ],
    sentiment: 'positive', sentimentScore: 0.95,
    outcome: { kind: 'praise_logged', toUserId: 'user-004' },
    ...base({ createdAt: hoursAgo(36), updatedAt: hoursAgo(35.99) }),
  },
];

export const VOICE_SEED_SUMMARY = { voice_calls: SEED_VOICE_CALLS.length };
